import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect, createContext } from 'react';
import axios from 'axios';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import '@/i18n';
import { ThemeProvider } from './contexts/ThemeContext';
import { supabase } from './lib/supabase';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import SEOCopilot from './pages/SEOCopilot';
import Campaigns from './pages/Campaigns';
import AIInsights from './pages/AIInsights';
import HealthScore from './pages/HealthScore';
import Profile from './pages/Profile';
import Pricing from './pages/Pricing';
import WhatsAppROI from './pages/WhatsAppROI';
import AboutUs from '@/pages/AboutUs';
import Support from '@/pages/Support';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CampaignMatrix from './pages/CampaignMatrix';
import TermsOfService from './pages/TermsOfService';
import DataDeletion from './pages/DataDeletion';
import Subscription from './pages/Subscription';
import UploadCSV from './pages/UploadCSV';
import Clients from './pages/Clients';
import ClientWorkspace from './pages/ClientWorkspace';
import Reports from './pages/Reports';
import MarketingStudio from './pages/MarketingStudio';
import MarketingStudioPage from './pages/MarketingStudioPage';
import ScheduledReports from './pages/ScheduledReports';
import Alerts from './pages/Alerts';
import Comparison from './pages/Comparison';
import OrganicDashboard from './pages/OrganicDashboard';
import AdminDashboard from './pages/AdminDashboard';
// ===== Refactor v2 multi-tenancy =====
import BrandsList from './pages/v2/BrandsList';
import BrandDetail from './pages/v2/BrandDetail';
import OAuthCallback from './pages/v2/OAuthCallback';
import GoogleAdsAnalysis from './pages/GoogleAdsAnalysis';
import KeywordIntelligence from './pages/KeywordIntelligence';
import Branding from './pages/Branding';
import CustomDashboard from './pages/CustomDashboard';
import AuthCallback from './components/AuthCallback';
import Integrations from './pages/Integrations';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext();
import { WorkspaceProvider, useWorkspace } from '@/contexts/WorkspaceContext';
import { WorkspacesV2Provider } from '@/contexts/WorkspacesV2Context';
import { DateRangeProvider } from '@/contexts/DateRangeContext';

function AppRouter({ children }) {
  const location = useLocation();
  // Refactor Fase 9: detectar tanto session_id= (flow legacy) como
  // access_token= (flow Supabase Auth) en el hash de la URL.
  const hash = location.hash || '';
  if (hash.includes('access_token=') || hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Refactor Fase 9: bootstrap desde Supabase Auth + listener de cambios
  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      try {
        // 1) Restaurar sesion de Supabase si existe
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          localStorage.setItem('token', session.access_token);
          if (mounted) setToken(session.access_token);
          await fetchUser(session.access_token);
        } else {
          // No hay sesion -> limpiar token huerfano
          localStorage.removeItem('token');
          if (mounted) {
            setToken(null);
            setUser(null);
          }
        }
      } catch (e) {
        console.error('Bootstrap auth error:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    bootstrap();

    // 2) Listener para cambios de sesion (login, logout, refresh token)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session?.access_token) {
          localStorage.setItem('token', session.access_token);
          setToken(session.access_token);
          await fetchUser(session.access_token);
        }
      } else if (event === 'SIGNED_OUT') {
        localStorage.removeItem('token');
        localStorage.removeItem('adpulse_workspace');
        localStorage.removeItem('adpulse_client_id');
        setToken(null);
        setUser(null);
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const fetchUser = async (overrideToken) => {
    try {
      const t = overrideToken || token || localStorage.getItem('token');
      if (!t) return;
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      setUser(response.data);
      window.__adpulse_user_email = response.data?.email || '';
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    }
  };

  // Refactor Fase 9: login() ahora solo persiste (Supabase maneja la sesion)
  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    localStorage.removeItem('adpulse_workspace');
    localStorage.removeItem('adpulse_client_id');
    setToken(newToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn('supabase.auth.signOut() failed:', e);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('adpulse_workspace');
    localStorage.removeItem('adpulse_client_id');
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-xl">Loading...</div>
      </div>
    );
  }

  function DashboardWithKey() {
    const { clientId } = useWorkspace();
    return <Dashboard key={clientId || 'default'} />;
  }

  function DashboardWithKey() {
    const { clientId } = useWorkspace();
    return <Dashboard key={clientId || 'default'} />;
  }

  return (
    <HelmetProvider>
    <ThemeProvider>
    <AuthContext.Provider value={{ user, token, login, logout }}>
      <WorkspaceProvider token={token}>
      <WorkspacesV2Provider>
      <DateRangeProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter>
            <Routes>
              <Route path="/" element={!user ? <Landing /> : <Navigate to="/dashboard" />} />
              <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
              <Route path="/register" element={!user ? <Register /> : <Navigate to="/dashboard" />} />
              <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
              <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/dashboard" />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<AboutUs />} />
            <Route path="/support" element={<Support />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/marketing-studio" element={user ? <MarketingStudio /> : <Navigate to="/login" />} />
              <Route path="/studio" element={user ? <MarketingStudioPage /> : <Navigate to="/login" />} />
              <Route path="/dashboard" element={user ? <DashboardWithKey /> : <Navigate to="/login" />} />
              {/* ===== Refactor v2 ===== */}
              <Route path="/v2/brands" element={user ? <BrandsList /> : <Navigate to="/login" />} />
              <Route path="/v2/brands/:id" element={user ? <BrandDetail /> : <Navigate to="/login" />} />
              <Route path="/v2/oauth/callback" element={<OAuthCallback />} />
              <Route path="/analytics" element={user ? <Analytics /> : <Navigate to="/login" />} />
              <Route path="/seo-copilot" element={user ? <SEOCopilot /> : <Navigate to="/login" />} />
              <Route path="/keyword-intelligence" element={user ? <KeywordIntelligence /> : <Navigate to="/login" />} />
              <Route path="/google-ads-analysis" element={user ? <GoogleAdsAnalysis /> : <Navigate to="/login" />} />
              <Route path="/campaigns" element={user ? <Campaigns /> : <Navigate to="/login" />} />
              <Route path="/ai-insights" element={user ? <AIInsights /> : <Navigate to="/login" />} />
              <Route path="/health-score" element={user ? <HealthScore /> : <Navigate to="/login" />} />
              <Route path="/upload" element={user ? <UploadCSV /> : <Navigate to="/login" />} />
              <Route path="/clients" element={user ? <Clients /> : <Navigate to="/login" />} />
              <Route path="/clients/:id" element={user ? <ClientWorkspace /> : <Navigate to="/login" />} />
              <Route path="/reports" element={user ? <Reports /> : <Navigate to="/login" />} />
              <Route path="/scheduled-reports" element={user ? <ScheduledReports /> : <Navigate to="/login" />} />
              <Route path="/alerts" element={user ? <Alerts /> : <Navigate to="/login" />} />
              <Route path="/organic" element={user ? <OrganicDashboard /> : <Navigate to="/login" />} />
              <Route path="/panel-admin" element={user ? <AdminDashboard /> : <Navigate to="/login" />} />
              <Route path="/comparison" element={user ? <Comparison /> : <Navigate to="/login" />} />
              <Route path="/branding" element={user ? <Branding /> : <Navigate to="/login" />} />
              <Route path="/custom-dashboard" element={user ? <CustomDashboard /> : <Navigate to="/login" />} />
              <Route path="/matrix" element={user ? <CampaignMatrix /> : <Navigate to="/login" />} />
              <Route path="/subscription" element={user ? <Subscription /> : <Navigate to="/login" />} />
              <Route path="/profile" element={user ? <Profile /> : <Navigate to="/login" />} />
              <Route path="/integrations" element={user ? <Integrations /> : <Navigate to="/login" />} />
              <Route path="/whatsapp-roi" element={user ? <WhatsAppROI /> : <Navigate to="/login" />} />
            </Routes>
          </AppRouter>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
      </DateRangeProvider>
      </WorkspacesV2Provider>
      </WorkspaceProvider>
    </AuthContext.Provider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
